// BranchLocation.jsx
import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import utc from "dayjs/plugin/utc";
import BranchFormModal from "./BranchFormModal";
import BranchViewModal from "./BranchViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);

const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

const BranchLocation = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [branchType, setBranchType] = useState("All");
  const [status, setStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // --- ADD THESE THREE NEW STATE VARIABLES ---
const [isSyncing, setIsSyncing] = useState(false);
const [showSyncSuccess, setShowSyncSuccess] = useState(false);
const [syncData, setSyncData] = useState(null);

  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // THEME CSS VARIABLES (match VendorMaster exactly)
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      // status colors (table badge / pill specific variables added)
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      // TD-specific badge colors (sensible defaults)
      "--status-active-bg-td": "#a2eddb",
      "--status-active-color-td": "#2a6a56",
      "--status-inactive-bg-td": "#fff4d6",
      "--status-inactive-color-td": "#7a5a00",
      // page & table header
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444",
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    const styleId = "branch-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        /* primary button uses accent color */
        .primary-btn {
          background-color: var(--accent-color);
          color: #ffffff;
         
        }
        .primary-btn:hover { background-color: var(--accent-hover); }

        /* white secondary button (export) */
        .secondary-btn {
          background-color: var(--surface-1);
          color: var(--text-primary);
          border: 1px solid var(--page-border-color);
          
        }
        .secondary-btn:hover { background-color: #f8faf9; }

        /* status badges */
        .status-active {
          background-color: var(--status-active-bg);
          color: #ffffff;
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }
        .status-active:hover { filter: brightness(0.98); }


         .status-active-td {
          background-color: var(--status-active-bg-td);
          color: var(--status-active-color-td);
          border-radius: 12px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }

        .status-inactive {
          background-color: var(--status-inactive-bg);
          color: var(--status-inactive-color);
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }
        .status-inactive:hover { filter: brightness(0.98); }

        .status-inactive-td {
          background-color: var(--status-inactive-bg-td);
          color: var(--status-inactive-color-td);
          border-radius: 12px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }

.cust-status{
        
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

          .cust-status-in{
          
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

        /* icons that should use accent color */
        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        /* sort arrow active color */
        .sort-active { color: var(--accent-color) !important; }
        .sort-inactive { color: var(--sort-inactive) !important; }

        /* pagination active */
        .page-active { background-color: var(--accent-color); color: #fff;  }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        /* small helper so icon-only buttons still get hover transition */
        .icon-btn { transition: color .15s ease; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/branches-filter`, {headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
    setLoading(false);
  };

  const handleSync = async () => {
  if (isSyncing) return;
  setIsSyncing(true);
  
  try {
    // Make sure this URL matches your actual backend route for Branch Sync
    const res = await fetch(`${API_URL}/mapping/bc-branches/sync`, { 
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    const data = await res.json();

    if (data.success) {
      setSyncData({
        total: data.recordCount,
        inserted: data.inserted,
        updated: data.updated
      });
      setShowSyncSuccess(true);
      fetchBranches(); // Refresh the list
    } else {
      alert(`Sync Failed: ${data.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error("Sync Error:", error);
    alert("An error occurred while syncing branches.");
  } finally {
    setIsSyncing(false);
  }
};


  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedBranches = [...branches].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;

      if (key === "CreatedAt") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }

      if (key === "BranchCode") {
        try {
          const aNum = parseInt((a[key] || "").split("-")[1], 10) || 0;
          const bNum = parseInt((b[key] || "").split("-")[1], 10) || 0;
          return direction === "asc" ? aNum - bNum : bNum - aNum;
        } catch {
          // fallback to string compare
        }
      }

      const av = (a[key] ?? "").toString().toLowerCase();
      const bv = (b[key] ?? "").toString().toLowerCase();
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setBranches(sortedBranches);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" style={{ color: "var(--sort-inactive)" }} />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ml-1 w-3 h-3 sort-active" />
    ) : (
      <FaSortDown className="ml-1 w-3 h-3 sort-active" />
    );
  };

  const handleView = (branch) => {
    setSelectedBranch(branch);
    setShowView(true);
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch.BranchCode);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (branch) => {
    if (!window.confirm("Are you sure you want to deactivate this branch?")) return;

    try {
      const formData = new FormData();
      formData.append("branchName", branch.BranchName);
      formData.append("branchType", branch.BranchType || "");
      formData.append("parentBranchCode", branch.ParentBranchCode || "");
      formData.append("addressLine1", branch.AddressLine1 || "");
      formData.append("addressLine2", branch.AddressLine2 || "");
      formData.append("city", branch.City || "");
      formData.append("state", branch.State || "");
      formData.append("country", branch.Country || "");
      formData.append("pincode", branch.Pincode || "");
      formData.append("phoneNumber", branch.PhoneNumber || "");
      formData.append("email", branch.Email || "");
      formData.append("contactPersonName", branch.ContactPersonName || "");
      formData.append("gstNumber", branch.GSTNumber || "");
      formData.append("panNumber", branch.PANNumber || "");
      formData.append("registrationNo", branch.RegistrationNo || "");
      formData.append("isActive", "0");
      formData.append("isDefault", branch.IsDefault ? "1" : "0");
      formData.append("timezone", branch.Timezone || "");
      formData.append("currencyCode", branch.CurrencyCode || "");
      formData.append("updatedBy", "1");

      const response = await fetch(`${API_URL}/branch/${branch.BranchCode}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to deactivate branch");
      }

      fetchBranches();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error deactivating branch:", error);
      alert(error.message || "An error occurred while deactivating the branch");
    }
  };

    const handleLogout = () => {
    navigate('/login');
  };

  const handleReactivate = async (branch) => {
    if (!window.confirm("Are you sure you want to reactivate this branch?")) return;

    try {
      const formData = new FormData();
      formData.append("branchName", branch.BranchName);
      formData.append("branchType", branch.BranchType || "");
      formData.append("parentBranchCode", branch.ParentBranchCode || "");
      formData.append("addressLine1", branch.AddressLine1 || "");
      formData.append("addressLine2", branch.AddressLine2 || "");
      formData.append("city", branch.City || "");
      formData.append("state", branch.State || "");
      formData.append("country", branch.Country || "");
      formData.append("pincode", branch.Pincode || "");
      formData.append("phoneNumber", branch.PhoneNumber || "");
      formData.append("email", branch.Email || "");
      formData.append("contactPersonName", branch.ContactPersonName || "");
      formData.append("gstNumber", branch.GSTNumber || "");
      formData.append("panNumber", branch.PANNumber || "");
      formData.append("registrationNo", branch.RegistrationNo || "");
      formData.append("isActive", "1");
      formData.append("isDefault", branch.IsDefault ? "1" : "0");
      formData.append("timezone", branch.Timezone || "");
      formData.append("currencyCode", branch.CurrencyCode || "");
      formData.append("updatedBy", "1");

      const response = await fetch(`${API_URL}/branch/${branch.BranchCode}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reactivate branch");
      }

      fetchBranches();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error reactivating branch:", error);
      alert(error.message || "An error occurred while reactivating the branch");
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      "BranchCode",
      "BranchName",
      "BranchType",
      "Country",
      "State",
      "City",
      "Status",
      "CreatedAt",
    ];

    const rows = branches.map((b) => [
      b.BranchCode,
      b.BranchName,
      b.BranchType,
      b.Country,
      b.State,
      b.City,
      b.IsActive ? "Active" : "Inactive",
      dayjs.utc(b.CreatedAt).format("YYYY-MM-DD HH:mm:ss"),
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "branches.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedBranch(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return;
    setFormLoading(true);

    try {
      if (isEdit && selectedBranch) {
        const response = await fetch(`${API_URL}/branch/${selectedBranch}`, {
          method: "PUT",
          headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update branch");
        }
      } else {
        const response = await fetch(`${API_URL}/branch`, {
          method: "POST",
          headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.message && errorData.message.includes("duplicate key")) {
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert("Duplicate branch code detected. The form has been refreshed with a new branch code. Please try again.");
            }, 100);
            return;
          }
          throw new Error(errorData.message || "Failed to create branch");
        }
      }

      setShowForm(false);
      fetchBranches();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving branch:", error);
      alert(error.message || "An error occurred while saving the branch");
    } finally {
      setFormLoading(false);
    }
  };

  const filteredBranches = branches.filter((branch) => {
    const matchesSearch = searchTerm === "" || 
      branch.BranchCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.BranchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (branch.Country && branch.Country.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (branch.State && branch.State.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (branch.City && branch.City.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesBranchType = branchType === "All" || branch.BranchType === branchType;
    const matchesStatus =
      status === "All" ||
      (status === "Active" && branch.IsActive) ||
      (status === "Inactive" && !branch.IsActive);

    return matchesSearch && matchesBranchType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const branchesForPage = filteredBranches.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "page-inactive"}`}
      >
        <LeftArrowIcon className="w-4 h-4" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === currentPage;
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-1 font-medium transition-all duration-200  ${isActive ? "page-active" : "page-inactive"}`}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? "text-gray-400 cursor-not-allowed" : "page-inactive"}`}
      >
        <RightArrowIcon className="w-4 h-4" />
      </button>
    );

    return pages;
  };

  const branchTypes = [...new Set(branches.map((b) => b.BranchType).filter(Boolean))];

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission("BRANCH_MASTER", "CanCreate");
  const canView = hasFullAccess || hasPermission("BRANCH_MASTER", "CanView");
  const canEdit = hasFullAccess || hasPermission("BRANCH_MASTER", "CanEdit");
  const canDelete = hasFullAccess || hasPermission("BRANCH_MASTER", "CanDelete");

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: "var(--page-bg)" }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4" style={{ borderColor: "var(--accent-color)" }}></div>
          <p className="mt-4 text-lg font-medium" style={{ color: "var(--text-muted)" }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Theme object passed to Sidebar so it matches this page's accent/profile color
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--page-bg)" }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} 
      onLogout={handleLogout}/>
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>

        {/* Header Section */}
        <header className="mb-8 pb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300" >
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "var(--text-primary)" }}>Branch Master</h1>
            <p className="mt-2" style={{ color: "var(--text-muted)" }}>Manage your business branches and facilities</p>
          </div>
          <div className="flex items-center gap-3">
      
            {/* --- ADD SYNC BUTTON HERE --- */}
            <button 
              onClick={handleSync} 
              disabled={isSyncing}
              className={`flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm border ${
                isSyncing 
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
              }`}
            >
              {isSyncing ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <FaCheck className="h-4 w-4" />
                  Sync 
                </>
              )}
            </button>
 
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium transition-all duration-200 shadow-sm"
            >
              <FaDownload className="h-4 w-4" />
              Export
            </button>

            
            {/* {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm rounded-none"
              >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )} */}
          </div>
        </header>

        {/* Stats Cards (sharp) */}
        {/* <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>Total Branches</p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{branches.length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: "var(--surface-2)", color: "var(--text-primary)", borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>Active Branches</p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{branches.filter((b) => b.IsActive).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: "var(--status-active-bg)", color: "var(--status-active-color)", borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>Inactive Branches</p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{branches.filter((b) => !b.IsActive).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: "var(--status-inactive-bg)", color: "var(--status-inactive-color)", borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>This Month</p>
                <p className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                  {branches.filter((b) => {
                    const createdDate = new Date(b.CreatedAt);
                    const now = new Date();
                    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: "var(--surface-2)", color: "var(--text-primary)", borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
            </div>
          </div>
        </section> */}

        {/* Search and Filters (sharp) */}
        <section className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Search Branches</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: "var(--search-icon)" }} />
                <input
                  type="text"
                  placeholder="Search by branch code, name, city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10"
                  style={{ backgroundColor: "var(--surface-1)", border: `1px solid var(--divider)`, color: "var(--text-primary)" }}
                />
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Branch Type</label>
              <select
                value={branchType}
                onChange={(e) => setBranchType(e.target.value)}
                className="px-4 py-2"
                style={{ backgroundColor: "var(--surface-1)", border: `1px solid var(--divider)`, color: "var(--text-primary)" }}
              >
                <option>All</option>
                {branchTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div> */}

            {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2"
                style={{ backgroundColor: "var(--surface-1)", border: `1px solid var(--divider)`, color: "var(--text-primary)" }}
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div> */}

            <button className="px-5 py-2 primary-btn font-medium rounded-none" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </section>

        {/* Branches Table (attached to search & stats — sharp) */}
        <section className="bg-[color:var(--surface-1)] border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300" >
            <h2 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Branch Summary</h2>
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: "var(--text-muted-2)" }}>Loading branches...</div>
          ) : branchesForPage.length === 0 ? (
            <div className="p-8 text-center" style={{ color: "var(--text-muted-2)" }}>No branches found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: "var(--table-header-bg)" }} className="text-[color:var(--text-muted)] text-xs font-semibold uppercase">
                  <tr>
                    <th className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("BranchCode")}>
                      <div className="flex items-center">Branch Code {getSortIcon("BranchCode")}</div>
                    </th>
                    <th className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("BranchName")}>
                      <div className="flex items-center">Branch Name {getSortIcon("BranchName")}</div>
                    </th>
                    {/* <th className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("BranchType")}>
                      <div className="flex items-center">Branch Type {getSortIcon("BranchType")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("Country")}>
                      <div className="flex items-center">Country {getSortIcon("Country")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("State")}>
                      <div className="flex items-center">State {getSortIcon("State")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("City")}>
                      <div className="flex items-center">City {getSortIcon("City")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("IsActive")}>
                      <div className="flex items-center">Status {getSortIcon("IsActive")}</div>
                    </th>
                    <th className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`} onClick={() => handleSort("CreatedAt")}>
                      <div className="flex items-center">Created_at {getSortIcon("CreatedAt")}</div>
                    </th> */}
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderTop: "1px solid var(--divider)" }}>
                  {branchesForPage.map((branch, idx) => (
                    <tr key={branch.BranchCode || idx} className={`hover:bg-[color:var(--surface-2)] transition-colors duration-200 ${idx % 2 === 0 ? "bg-[color:var(--surface-1)]" : "bg-[color:var(--surface-2)]"}`}>
                      <td className="px-6 py-3 font-mono" style={{ color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                        <p className={`transition-all duration-200 ${!canView ? "pointer-events-none" : "cursor-pointer"}`} onClick={() => canView && handleView(branch)}>
                          {branch.BranchCode}
                        </p>
                      </td>
                      <td className="px-6 py-3" style={{ color: "var(--text-primary)", whiteSpace: "nowrap", fontWeight: 600 }}>{branch.BranchName}</td>
                      {/* <td className="px-6 py-3" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{branch.BranchType || "-"}</td>
                      <td className="px-4 py-3" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{branch.Country || "-"}</td>
                      <td className="px-4 py-3" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{branch.State || "-"}</td>
                      <td className="px-4 py-3" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{branch.City || "-"}</td>
                      <td className="px-4 py-3">
                        <span className= {` rounded-full ${branch.IsActive ? ' cust-status bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold' : ' cust-status-in bg-amber-50 text-amber-700 font-bold hover:bg-amber-200 '}`}>
                          
                          {branch.IsActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                        {branch.CreatedAt ? (
                          <div className="flex flex-col leading-tight">
                            <span style={{ color: "var(--text-primary)" }}>{dayjs.utc(branch.CreatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs" style={{ color: "var(--text-muted-2)" }}>{dayjs.utc(branch.CreatedAt).format("HH:mm:ss")}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td> */}
                      <td className="px-6 py-3 flex gap-2 justify-center items-center">
                        {canView && (
                          <button title="View" className="p-2 rounded-full icon-btn" onClick={() => handleView(branch)} style={{ color: "var(--accent-color)" }}>
                            <FaEye />
                          </button>
                        )}
                        {/* {canEdit && (
                          <button title="Edit" className="p-2 rounded-full icon-btn" onClick={() => handleEdit(branch)} style={{ color: "var(--accent-color)" }}>
                            <FaEdit />
                          </button>
                        )} */}
                        {/* {canDelete && (
                          <>
                            {branch.IsActive ? (
                              <button title="Deactivate" className="p-2 rounded-full icon-btn" onClick={() => handleDeactivate(branch)} style={{ color: "var(--danger)" }}>
                                <FaBan />
                              </button>
                            ) : (
                              <button title="Reactivate" className="p-2 rounded-full icon-btn" onClick={() => handleReactivate(branch)} style={{ color: "var(--accent-color)" }}>
                                <FaCheck />
                              </button>
                            )}
                          </>
                        )} */}
                        {/* <button title="Download CSV" className="p-2 rounded-full icon-btn" onClick={handleDownloadCSV} style={{ color: "var(--text-muted)" }}>
                          <FaDownload />
                        </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* --- ADD SYNC SUCCESS MODAL HERE --- */}
        {showSyncSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all scale-100">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <FaCheck className="h-8 w-8 text-green-600" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
              <p className="text-gray-500 mb-6">The branch data has been successfully synchronized.</p>

              {/* Optional: Show sync stats */}
              {/* <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                <div>
                  <span className="block font-bold text-gray-900">{syncData?.total || 0}</span>
                  <span className="text-xs text-gray-500 uppercase">Total</span>
                </div>
                <div>
                  <span className="block font-bold text-green-600">{syncData?.inserted || 0}</span>
                  <span className="text-xs text-gray-500 uppercase">Inserted</span>
                </div>
                <div>
                  <span className="block font-bold text-blue-600">{syncData?.updated || 0}</span>
                  <span className="text-xs text-gray-500 uppercase">Updated</span>
                </div>
              </div> */}

              <button
                onClick={() => setShowSyncSuccess(false)}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white primary-btn shadow-sm hover:shadow focus:outline-none transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        <BranchFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedBranch}
          isEdit={isEdit}
          loading={formLoading}
        />

      <BranchViewModal
          open={showView}
          onClose={() => setShowView(false)}
          branch={selectedBranch}
        />
      </main>
    </div>
  );
};

export default BranchLocation;
